/**
 * Hook for fetching relationships for an Issue
 * Gets all related Trends, Hypotheses, Data Elements, and Best Practices
 */

import { useQuery } from '@tanstack/react-query';
import { getItemRelationships, type ResearchRelationship } from '../../../lib/api/domainPacksApi';
import type { Issue, Trend, BestPractice, DataElement, Hypothesis, Subdomain } from '../../../types/domainPack';

// Types for the canvas
export interface RelatedTrend {
  trend: Trend;
  relationship: {
    type: string;
    strength: string;
    explanation: string;
  };
}

export interface RelatedHypothesis {
  hypothesis: Hypothesis;
  relationship: {
    type: string;
    strength: string;
    explanation: string;
  };
}

export interface RelatedBestPractice {
  practice: BestPractice;
  relationship: {
    type: string;
    strength: string;
    explanation: string;
  };
}

export interface RelatedDataElement {
  dataElement: DataElement;
  relationship: {
    type: string;
    strength: string;
    explanation: string;
  };
}

export interface IssueRelationships {
  issue: Issue;
  trends: RelatedTrend[];
  hypotheses: RelatedHypothesis[];
  bestPractices: RelatedBestPractice[];
  dataElements: RelatedDataElement[];
}

/**
 * Extracts related items from relationships and subdomain data
 *
 * Note: The API returns different structures for outgoing vs incoming:
 * - Outgoing: has targetId, targetType, targetTitle (source is the queried item)
 * - Incoming: has sourceId, sourceType, sourceTitle (target is the queried item)
 */
function extractRelatedItems(
  issue: Issue,
  relationships: { outgoing: ResearchRelationship[]; incoming: ResearchRelationship[] },
  subdomain: Subdomain
): IssueRelationships {
  const trends: RelatedTrend[] = [];
  const hypotheses: RelatedHypothesis[] = [];
  const bestPractices: RelatedBestPractice[] = [];
  const dataElements: RelatedDataElement[] = [];

  // Process outgoing relationships (issue -> other item)
  // For outgoing, the related item is the target
  relationships.outgoing.forEach((rel) => {
    const relatedId = rel.targetId;
    const relatedType = rel.targetType;

    const relationshipInfo = {
      type: rel.relationshipType,
      strength: rel.strength,
      explanation: rel.explanation,
    };

    addRelatedItem(relatedType, relatedId, relationshipInfo, subdomain, trends, hypotheses, bestPractices, dataElements);
  });

  // Process incoming relationships (other item -> issue)
  // For incoming, the related item is the source
  relationships.incoming.forEach((rel) => {
    const relatedId = rel.sourceId;
    const relatedType = rel.sourceType;

    const relationshipInfo = {
      type: rel.relationshipType,
      strength: rel.strength,
      explanation: rel.explanation,
    };

    addRelatedItem(relatedType, relatedId, relationshipInfo, subdomain, trends, hypotheses, bestPractices, dataElements);
  });

  return {
    issue,
    trends,
    hypotheses,
    bestPractices,
    dataElements,
  };
}

/**
 * Helper function to add a related item to the appropriate array
 */
function addRelatedItem(
  relatedType: string,
  relatedId: string,
  relationshipInfo: { type: string; strength: string; explanation: string },
  subdomain: Subdomain,
  trends: RelatedTrend[],
  hypotheses: RelatedHypothesis[],
  bestPractices: RelatedBestPractice[],
  dataElements: RelatedDataElement[]
): void {
  switch (relatedType) {
    case 'Trend':
      const trend = subdomain.trends.find(t => t.trend_id === relatedId);
      if (trend) {
        trends.push({ trend, relationship: relationshipInfo });
      }
      break;

    case 'Hypothesis':
      const hypothesis = subdomain.hypotheses.find(h => h.hypothesis_id === relatedId);
      if (hypothesis) {
        hypotheses.push({ hypothesis, relationship: relationshipInfo });
      }
      break;

    case 'BestPractice':
      const practice = subdomain.practices.find(p => p.practice_id === relatedId);
      if (practice) {
        bestPractices.push({ practice, relationship: relationshipInfo });
      }
      break;

    case 'DataElement':
      const dataElement = subdomain.data_elements.find(d => d.element_id === relatedId);
      if (dataElement) {
        dataElements.push({ dataElement, relationship: relationshipInfo });
      }
      break;
  }
}

/**
 * Hook to fetch relationships for a specific issue
 */
export function useIssueRelationships(
  domainPackId: string | undefined,
  issue: Issue | null,
  subdomain: Subdomain | null
) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['issue-relationships', domainPackId, issue?.issue_id],
    queryFn: async () => {
      if (!domainPackId || !issue || !subdomain) {
        throw new Error('Missing required parameters');
      }

      const response = await getItemRelationships(
        domainPackId,
        'Issue',
        issue.issue_id,
        'both'
      );

      return extractRelatedItems(issue, response, subdomain);
    },
    enabled: !!domainPackId && !!issue && !!subdomain,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    relationships: data,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Utility to get color for relationship strength
 */
export function getStrengthColor(strength: string): string {
  switch (strength) {
    case 'high':
      return 'text-green-600';
    case 'medium':
      return 'text-yellow-600';
    case 'low':
      return 'text-red-600';
    default:
      return 'text-neutral-600';
  }
}

/**
 * Utility to get background color for relationship strength
 */
export function getStrengthBgColor(strength: string): string {
  switch (strength) {
    case 'high':
      return 'bg-green-50 border-green-200';
    case 'medium':
      return 'bg-yellow-50 border-yellow-200';
    case 'low':
      return 'bg-red-50 border-red-200';
    default:
      return 'bg-neutral-50 border-neutral-200';
  }
}
